<div class="mt-6 w-full flex flex-col gap-6">
  <p-toast />
  <p-confirmdialog />

  <!-- Header -->
  <div class="flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1>
        {{ 'label.tasks' | translate }}
      </h1>
      <p class="mt-1 mb-0 text-surface-500">
        {{
          'task.summary'
            | translate: { open: counts().open, overdue: counts().overdue }
        }}
      </p>
    </div>
    <p-button
      icon="fa fa-plus"
      [label]="'task.add' | translate"
      (onClick)="navigateToCreate()"
    />
  </div>

  <!-- Toolbar -->
  <div class="flex flex-wrap items-center gap-3">
    <p-iconfield class="grow md:grow-0 md:w-80">
      <p-inputicon styleClass="fa fa-search" />
      <input
        pInputText
        type="text"
        class="w-full"
        [ngModel]="search()"
        (ngModelChange)="search.set($event)"
        [placeholder]="'task.search' | translate"
        [attr.aria-label]="'task.search' | translate"
      />
    </p-iconfield>

    <!-- Status tabs -->
    <p-selectbutton
      [options]="tabs"
      [ngModel]="statusTab()"
      (ngModelChange)="statusTab.set($event)"
      [allowEmpty]="false"
      [ariaLabel]="'task.status' | translate"
    >
      <ng-template #item let-tab>
        <span class="flex items-center gap-2">
          {{ 'task.tabs.' + tab | translate }}
          <p-badge [value]="countFor(tab)" severity="secondary" />
        </span>
      </ng-template>
    </p-selectbutton>

    <p-togglebutton
      [ngModel]="onlyMine()"
      (ngModelChange)="onlyMine.set($event)"
      [onLabel]="'task.onlyMine' | translate"
      [offLabel]="'task.onlyMine' | translate"
      onIcon="fa-regular fa-user"
      offIcon="fa-regular fa-user"
    />

    <!-- Priority -->
    <div class="flex items-center gap-2">
      <span class="text-surface-500">{{
        'task.priorityFilter' | translate
      }}</span>
      <p-selectbutton
        [options]="priorityChips"
        [ngModel]="priority()"
        (ngModelChange)="priority.set($event)"
        [allowEmpty]="false"
        [ariaLabel]="'task.priorityFilter' | translate"
      >
        <ng-template #item let-p>
          @if (p === 'all') {
            {{ 'task.allPriorities' | translate }}
          } @else {
            {{ priorityLabelKey(p) | translate }}
          }
        </ng-template>
      </p-selectbutton>
    </div>
  </div>

  <!-- Task list -->
  <div
    class="rounded-2xl bg-surface-0 shadow-sm border border-surface-200 overflow-hidden"
  >
    @for (group of groups(); track group.key) {
      <div
        class="flex items-center gap-2 px-6 pt-5 pb-3 border-b border-surface-200"
      >
        <span
          class="text-sm font-bold uppercase tracking-wider"
          [ngClass]="
            group.key === 'overdue' ? 'text-red-700' : 'text-surface-600'
          "
          >{{ 'task.groups.' + group.key | translate }}</span
        >
        <span
          class="text-xs px-2 py-0.5 rounded-full bg-surface-100 text-surface-600"
          >{{ group.tasks.length }}</span
        >
      </div>

      @for (
        task of group.tasks;
        track task.current_execution?.id ?? task.id;
        let last = $last
      ) {
        <div
          class="group flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-surface-50 transition-colors"
          [class.border-b]="!last"
          [class.border-surface-100]="!last"
          role="link"
          tabindex="0"
          (click)="navigateToDetail(task)"
          (keydown.enter)="navigateToDetail(task)"
        >
          <!-- Complete -->
          @let done = isFinished(task) || task.status === 'done';
          <p-button
            [icon]="done ? 'fa fa-check' : ''"
            [rounded]="true"
            [outlined]="!done"
            [severity]="done ? 'primary' : 'secondary'"
            size="small"
            styleClass="shrink-0 !w-8 !h-8 !p-0"
            [ariaLabel]="(done ? 'task.reopen' : 'task.markDone') | translate"
            [pTooltip]="(done ? 'task.reopen' : 'task.markDone') | translate"
            (onClick)="toggleDone(task); $event.stopPropagation()"
          />

          <!-- Title + meta -->
          <div class="flex flex-col gap-1 min-w-0 grow">
            <span
              class="text-lg font-semibold truncate"
              [ngClass]="
                isFinished(task)
                  ? 'line-through text-surface-400'
                  : 'text-surface-900'
              "
              >{{ task.title }}</span
            >
            <div
              class="flex flex-wrap items-center gap-x-5 gap-y-1 text-surface-500"
            >
              @if (task.due_at) {
                @let due = dueLabel(task);
                @let dueSoon = isDueSoon(task);
                <span
                  class="flex items-center gap-1.5"
                  [ngClass]="{
                    'text-red-700 font-semibold': isOverdue(task),
                    'text-amber-700 font-semibold': dueSoon,
                    'text-surface-900 font-semibold':
                      !isOverdue(task) &&
                      !dueSoon &&
                      due.key === 'task.due.today',
                  }"
                >
                  <i class="fa-regular fa-clock"></i>
                  {{ due.key ? (due.key | translate: due.params) : due.text }}
                </span>
                @if (dueSoon) {
                  <p-tag
                    icon="fa-regular fa-hourglass-end"
                    severity="warn"
                    [value]="'task.dueSoon' | translate"
                  />
                }
              }
              @if (task.is_recurring) {
                <span class="flex items-center gap-1.5">
                  <i class="fa-regular fa-repeat"></i>
                  {{
                    recurrenceLabelKey(
                      task.recurrence_unit!,
                      task.recurrence_every
                    ) | translate: { count: task.recurrence_every }
                  }}
                </span>
              }
              @if ((task.missed_count ?? 0) > 0 && !isFinished(task)) {
                <p-tag
                  icon="fa-regular fa-circle-exclamation"
                  severity="warn"
                  [value]="
                    'task.missedCount' | translate: { count: task.missed_count }
                  "
                />
              }
              @if (task.status === 'in_progress') {
                <span class="flex items-center gap-1.5 text-amber-700">
                  <i class="fa-regular fa-play"></i>
                  {{ 'task.statuses.in_progress' | translate }}
                </span>
              }
              @if (task.status === 'done' && task.requires_approval) {
                <span class="flex items-center gap-1.5">
                  <i class="fa-regular fa-hourglass-half"></i>
                  {{ 'task.awaitingApproval' | translate }}
                </span>
              }
            </div>
          </div>

          <!-- Priority -->
          <div class="hidden md:flex w-32 shrink-0">
            @if (task.priority) {
              <p-tag
                [rounded]="true"
                [severity]="prioritySeverity(task.priority)"
                [value]="priorityLabelKey(task.priority) | translate"
              />
            }
          </div>

          <!-- Assignee -->
          <div class="hidden lg:flex items-center gap-3 w-56 shrink-0">
            @if (task.responsible_user; as user) {
              <p-avatar
                shape="circle"
                [label]="initials(user.name)"
                [styleClass]="avatarClass(user.id)"
              />
              <span class="truncate text-surface-900">{{ user.name }}</span>
            } @else {
              <p-avatar
                shape="circle"
                icon="fa fa-user-plus"
                styleClass="!bg-transparent border border-dashed border-surface-400 text-surface-400"
              />
              <span class="truncate text-surface-500">{{
                'task.notAssigned' | translate
              }}</span>
            }
          </div>

          <!-- Delete -->
          <p-button
            icon="fa-regular fa-trash-can"
            [text]="true"
            [rounded]="true"
            severity="secondary"
            [ariaLabel]="'task.deleteHeader' | translate"
            (onClick)="onDelete($event, task); $event.stopPropagation()"
          />
        </div>
      }
    } @empty {
      <div class="px-6 py-12 text-center text-surface-500">
        {{ 'task.empty' | translate }}
      </div>
    }
  </div>
</div>
